import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { fetchAllPages } from '@/utils/fetch-all-pages';
import http from '@/utils/http.service';

/** Request size used to page through every site. */
const SITES_PAGE_SIZE = 100;

const siteOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type SiteOption = z.infer<typeof siteOptionSchema>;

const getAllSitesApiSchema = z.object({
  page: z.number(),
  per_page: z.number(),
  page_count: z.number(),
  data: z.array(siteOptionSchema),
});

export type GetAllSitesResponse = z.infer<typeof getAllSitesApiSchema>;

const getSitesPage = async (page: number): Promise<GetAllSitesResponse> => {
  return http.sites
    .getList({
      page,
      per_page: SITES_PAGE_SIZE,
    })
    .then<GetAllSitesResponse>(({ response, transform }) => {
      return transform.handle({
        response,
        attributes: {
          name: true,
        },
        schema: getAllSitesApiSchema,
      });
    });
};

const getAllSites = async (): Promise<SiteOption[]> => {
  const sites = await fetchAllPages(getSitesPage);

  return sites.data;
};

type UseGetAllSitesQueryOptions = {
  enabled?: boolean;
};

export const useGetAllSitesQuery = (
  options: UseGetAllSitesQueryOptions = {},
): UseQueryResult<SiteOption[], Error> => {
  const { enabled = true } = options;
  return useQuery({
    queryFn: getAllSites,
    queryKey: ['sites', 'all'],
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  });
};
